'use client'

import React, { useEffect, useRef, useState } from 'react'
import { NotificationPopup } from '@/components/NotificationPopup'
import { useNotificationPopups } from '@/hooks/useNotificationPopups'
import { createClientLogger } from '@/lib/logger/client'

const log = createClientLogger('NotificationPopupManager')

/**
 * Global component that polls server notifications and displays them as popups
 */
export function NotificationPopupManager() {
  const { notifications: popupNotifications, addNotification, dismissNotification } = useNotificationPopups()
  const [notifications, setNotifications] = useState<any[]>([])
  const shownNotificationIds = useRef<Set<string>>(new Set())

  useEffect(() => {
    // Find notifications that haven't been shown as popups yet
    const unshownNotifications = notifications.filter(n =>
      !shownNotificationIds.current.has(n.id)
    )

    // Display each unshown notification as a popup
    unshownNotifications.forEach(notification => {
      // Mark this notification as shown locally to prevent duplicate popups
      shownNotificationIds.current.add(notification.id)

      // Mark notification as read on the server when showing as toast
      fetch(`/api/notifications/read/${notification.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      }).catch(error => {
        log.error({ error, notificationId: notification.id }, 'Failed to mark notification as read')
      })

      // Determine notification type based on the notification data
      let popupType: 'info' | 'success' | 'warning' | 'error' = 'info'

      if (notification.type === 'moderation_approved') {
        popupType = 'success'
      } else if (notification.type === 'moderation_rejected') {
        popupType = 'warning'
      } else if (notification.type === 'mention') {
        popupType = 'info'
      }

      // Create a popup for this notification
      addNotification(
        popupType,
        notification.title || 'Nieuwe melding',
        notification.message || undefined,
        {
          duration: 5000, // Auto-dismiss after 5 seconds
          actions: notification.actions && notification.actions.length > 0 ?
            notification.actions
              .filter(action => action.url) // Only show actions with URLs
              .map(action => ({
                label: action.label,
                onClick: () => {
                  if (action.url) {
                    window.location.href = action.url
                  }
                },
                variant: 'primary' as const
              })) : undefined
        }
      )
    })
  }, [notifications, addNotification])

  // Poll server for notifications periodically
  useEffect(() => {
    let cancelled = false
    const fetchNotifications = async () => {
      try {
        const res = await fetch('/api/notifications', { credentials: 'include' })
        if (!res.ok) return
        const data = await res.json()
        const list = Array.isArray(data) ? data : (data.docs ?? data.notifications ?? [])
        if (!cancelled) setNotifications(list)
      } catch (e) {
        // ignore popup polling errors
      }
    }
    fetchNotifications()
    const id = setInterval(fetchNotifications, 30000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [])

  // Clean up shown notification IDs periodically to prevent memory leaks
  useEffect(() => {
    const interval = setInterval(() => {
      // Keep only IDs from the last 100 notifications
      const currentNotificationIds = new Set(notifications.map(n => n.id))
      shownNotificationIds.current = new Set(
        Array.from(shownNotificationIds.current).filter(id =>
          currentNotificationIds.has(id)
        )
      )
    }, 60000) // Clean up every minute

    return () => clearInterval(interval)
  }, [notifications])

  return (
    <NotificationPopup
      notifications={popupNotifications}
      onDismiss={dismissNotification}
      anchor="top-right"
    />
  )
}
